import React, {useState, useEffect} from 'react'
import {useSelector, useDispatch, shallowEqual} from 'react-redux'
import {RootState} from '../../../../setup'
import {
  getSubscriptionPlanList,
  getTeamSizeList,
  handlePageLoading,
  renewSubscriptionPlan,
} from '../../../../setup/redux/auth/actions'
import {upgradeSubscriptionPlan} from '../../../../setup/redux/my_purchases/actions'
import {GetCurrentActivePlan, isCompanyInTheGracePeriod} from '../../../../app/services/Functional'
import {handleNotificationWithToastify} from '../../../../app/services/Functional'
import SuccessDialog from './SuccessDialog'
import EnterpriseTeamSizeDialog from './EnterpriseTeamSizeDialog'
import SubscriptionPlanDisplay from '../../../components/common/SubscriptionPlanDisplay'
import Dialog from '../../../../_metronic/dialogs/CommonDialog'
import {handleLimitReachedDialog} from '../../../../setup/redux/users/actions'

interface UpgradePlanDialogProps {
  show: boolean
  onHide: () => void
  currentPlanBadgeText?: string
}

const UpgradePlanDialog: React.FC<UpgradePlanDialogProps> = ({
  show,
  onHide,
  currentPlanBadgeText = 'Current Plan',
}): JSX.Element => {
  const dispatch: any = useDispatch()
  const isExpiredPlan = currentPlanBadgeText === 'Expired Plan'
  const currentPlan = GetCurrentActivePlan(isExpiredPlan)
  const [selectedType, setSelectedType] = useState<'yearly' | 'monthly'>('monthly')
  const [showSuccessDialog, setShowSuccessDialog] = useState<boolean>(false)
  const [isUpgrading, setIsUpgrading] = useState<boolean>(false)
  const [showEnterpriseDialog, setShowEnterpriseDialog] = useState<boolean>(false)
  const [pendingEnterprisePlan, setPendingEnterprisePlan] = useState<any>(null)

  const {subscriptionPlans, active_inspection_company, expired_inspection_company}: any =
    useSelector<RootState>(
      (state) => ({
        subscriptionPlans: state.auth.subscriptionPlans,
        active_inspection_company: state.auth.active_inspection_company,
        expired_inspection_company: state.auth.expired_inspection_company,
      }),
      shallowEqual
    )

  const getUserPaidAmount = () => {
    if (!currentPlan || !currentPlan.subscription) {
      return 0
    }

    const currentSubscription = currentPlan.subscription
    const subscriptionType = currentPlan.subscription_type

    if (currentSubscription.type === 'free') {
      return 0
    }

    if (subscriptionType === 'yearly') {
      return currentSubscription.yearly_price || 0
    } else {
      return currentSubscription.monthly_price || 0
    }
  }

  const userPaidAmount = getUserPaidAmount()

  const filteredMonthlyPlans = React.useMemo(() => {
    if (!subscriptionPlans || !currentPlan)
      return subscriptionPlans.filter(
        (plan: any) => plan.type === 'free' || plan.type === 'paid' || plan.type === 'enterprise'
      )

    if (currentPlan.subscription && currentPlan.subscription.type !== 'free') {
      return subscriptionPlans.filter((plan: any) => {
        if (plan.type === 'free') return false
        if (plan.type === 'enterprise') return true
        if (
          currentPlan &&
          currentPlan.subscription &&
          currentPlan.subscription_type !== 'yearly' &&
          currentPlan.subscription.id === plan.id
        )
          return true
        return (plan.monthly_price || 0) > userPaidAmount
      })
    }

    if (currentPlan.subscription && currentPlan.subscription.type === 'free') {
      return subscriptionPlans.filter((plan: any) => {
        if (plan.type === 'free') {
          return plan.id === currentPlan.subscription.id
        }
        if (plan.type === 'enterprise') return true
        return (plan.monthly_price || 0) > userPaidAmount
      })
    }

    return subscriptionPlans.filter((plan: any) => {
      if (plan.type === 'free') return true
      if (plan.type === 'enterprise') return true
      if (currentPlan && currentPlan.subscription && currentPlan.subscription.id === plan.id)
        return true
      return (plan.monthly_price || 0) > userPaidAmount
    })
  }, [subscriptionPlans, currentPlan, userPaidAmount])

  const filteredYearlyPlans = React.useMemo(() => {
    if (!subscriptionPlans || !currentPlan)
      return subscriptionPlans.filter(
        (plan: any) => plan.type === 'paid' || plan.type === 'enterprise'
      )
    if (currentPlan.subscription && currentPlan.subscription.type !== 'free') {
      return subscriptionPlans.filter((plan: any) => {
        if (currentPlan && currentPlan.subscription && currentPlan.subscription.id === plan.id)
          return true
        if (plan.type === 'free') return false
        if (plan.type === 'enterprise') return true
        return (plan.yearly_price || 0) > userPaidAmount
      })
    }

    if (currentPlan.subscription && currentPlan.subscription.type === 'free') {
      return subscriptionPlans.filter((plan: any) => {
        if (currentPlan && currentPlan.subscription && currentPlan.subscription.id === plan.id)
          return true
        if (plan.type === 'free') {
          return plan.id === currentPlan.subscription.id
        }
        if (plan.type === 'enterprise') return true
        return (plan.yearly_price || 0) > userPaidAmount
      })
    }

    return subscriptionPlans.filter((plan: any) => {
      if (plan.type === 'enterprise') return true
      if (currentPlan && currentPlan.subscription && currentPlan.subscription.id === plan.id)
        return true
      return (plan.yearly_price || 0) > userPaidAmount
    })
  }, [subscriptionPlans, currentPlan, userPaidAmount])

  const filteredSubscriptionPlans =
    selectedType === 'monthly' ? filteredMonthlyPlans : filteredYearlyPlans

  useEffect(() => {
    if (show) {
      dispatch(getSubscriptionPlanList())
      dispatch(getTeamSizeList())
    }
  }, [show])

  const handleSelectType = (type: 'yearly' | 'monthly') => {
    setSelectedType(type)
  }

  const handleUpgrade = async (plan: any) => {
    if (plan.type === 'enterprise') {
      setPendingEnterprisePlan(plan)
      setShowEnterpriseDialog(true)
      onHide()
      return
    }

    await performUpgrade(plan)
  }

  const handleRenew = async (plan: any) => {
    setIsUpgrading(true)
    dispatch(handlePageLoading(true))
    await dispatch(
      renewSubscriptionPlan(
        isExpiredPlan ? expired_inspection_company?.id : active_inspection_company?.id
      )
    )
  }

  const performUpgrade = async (plan: any, teamSizeId?: string) => {
    setIsUpgrading(true)

    try {
      const payload: any = {
        inspection_company_id: isExpiredPlan
          ? expired_inspection_company?.id
          : active_inspection_company?.id,
        subscription_id: plan.id,
        subscription_type: selectedType,
      }

      if (plan.type === 'enterprise') {
        payload.is_enterprise_request = true
        payload.team_size_id = teamSizeId
        delete payload.subscription_type
      }

      const response = await dispatch(upgradeSubscriptionPlan(payload))

      if (response && response.success) {
        dispatch(handleLimitReachedDialog(false))
        if (plan.type === 'enterprise') {
          handleNotificationWithToastify({
            success: true,
            message: 'Enterprise plan upgrade request submitted successfully!',
          })
        } else {
          window.open(response.data.payment_link, '_self')
        }
        onHide()
      } else {
        handleNotificationWithToastify({
          success: false,
          message: response?.message || 'Failed to upgrade subscription plan',
        })
      }
    } catch (error: any) {
      console.error('Upgrade error:', error)
      handleNotificationWithToastify({
        success: false,
        message: error?.clientMessage || 'An error occurred while upgrading your plan',
      })
    } finally {
      setIsUpgrading(false)
    }
  }

  const handleTeamSizeSelect = async (teamSizeId: string) => {
    if (pendingEnterprisePlan) {
      await performUpgrade(pendingEnterprisePlan, teamSizeId)
      setPendingEnterprisePlan(null)
    }
    setShowEnterpriseDialog(false)
  }

  const handleEnterpriseDialogClose = () => {
    setShowEnterpriseDialog(false)
    setPendingEnterprisePlan(null)
  }

  const handleHideSuccessDialog = () => {
    setShowSuccessDialog(false)
  }

  return (
    <>
      <Dialog
        show={show}
        onHide={onHide}
        title='Upgrade Plan'
        size='xl'
        footer={undefined}
        modalClassName='custom-reschedule-dialog'
        headerClassname='custom-qr-dialog-header'
        bodyClassname=''
        footerClassname='border-0'
        centered
        backdrop='static'
        keyboard={false}
        body={
          <div className='dialog-container scrollable-upgrade-plan-dialog pb-0 mt-2'>
            <SubscriptionPlanDisplay
              selectedType={selectedType}
              onTypeChange={handleSelectType}
              showActionButtons={true}
              onUpgrade={handleUpgrade}
              onRenew={handleRenew}
              className=''
              subscriptionPlans={filteredSubscriptionPlans}
              disableHover={true}
              currentPlan={currentPlan}
              isUpgrading={isUpgrading}
              currentPlanBadgeText={currentPlanBadgeText}
              disableCursorPointer={true}
              isPreFiltered={true}
              isCompanyOnGracePeriod={
                isExpiredPlan
                  ? isCompanyInTheGracePeriod(expired_inspection_company)
                  : isCompanyInTheGracePeriod(active_inspection_company)
              }
            />
          </div>
        }
      />
      <SuccessDialog show={showSuccessDialog} onHide={handleHideSuccessDialog} />
      <EnterpriseTeamSizeDialog
        show={showEnterpriseDialog}
        onHide={handleEnterpriseDialogClose}
        onTeamSizeSelect={handleTeamSizeSelect}
        selectedPlan={pendingEnterprisePlan}
        selectedType={selectedType}
      />
    </>
  )
}

export default UpgradePlanDialog
